import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { getCookie } from "../../../../function.js";
import { API_URL } from "../../../../config/index.js";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Slide,
  IconButton,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import moment from "moment";
import getArticleService from "../../../../services/getArticleService";
import { Badge } from "react-bootstrap";
import { getPublicationColor } from "../../../../config/publicationColors";
import { getPaymentStatus } from "../../../../utils/paymentStatus";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const SingleSubmittedArticle = () => {
  const { id: rawId } = useParams();
  const id = decodeURIComponent(rawId);
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [openPdfDialog, setOpenPdfDialog] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageUrls, setImageUrls] = useState({});
  const [pdfUrls, setPdfUrls] = useState({});
  const [documentUrls, setDocumentUrls] = useState({});

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        setArticle(null);
        setImageUrls({});
        setPdfUrls({});
        setDocumentUrls({});

        const response = await axios.get(`${API_URL}/articles/${id}`, {
          headers: {
            Authorization: getCookie("token"),
            "x-api-key": "JjgeTensKk20J7GIxBfMSaA25Aricvmr1HYmjxpS",
          },
        });

        let parsedData = JSON.parse(response.data);
        let result = JSON.parse(parsedData.body);

        // Validate that we have valid data
        if (!result.data) {
          throw new Error("Invalid article data received");
        }

        // Remove clientDetails from the data
        const { clientDetails, ...articleData } = result.data;
        setArticle(articleData);

        // Fetch pre-signed URLs for all payment proof images and PDFs
        if (articleData.payments && articleData.payments.length > 0) {
          const urls = {};
          const pdfUrls = {};
          const docUrls = {};

          for (const payment of articleData.payments) {
            // Fetch payment proof images
            if (
              payment.paymentProofFileKeys &&
              payment.paymentProofFileKeys.length > 0
            ) {
              for (const proof of payment.paymentProofFileKeys) {
                try {
                  const fileResponse =
                    await getArticleService.getFilePreSIgnedUrl({ key: proof });
                  const fileType = getFileType(proof);

                  if (fileType === "image") {
                    urls[proof] = fileResponse;
                  } else if (fileType === "pdf") {
                    pdfUrls[proof] = fileResponse;
                  } else {
                    docUrls[proof] = fileResponse;
                  }
                } catch (error) {
                  console.error("Error fetching file URL:", error);
                }
              }
            }

            // Fetch invoice PDF
            if (payment.invoiceKey) {
              try {
                const pdfResponse = await getArticleService.getFilePreSIgnedUrl(
                  { key: payment.invoiceKey }
                );
                pdfUrls[payment.invoiceKey] = pdfResponse;
              } catch (error) {
                console.error("Error fetching PDF URL:", error);
              }
            }
          }

          // Fetch refund proof files
          if (
            articleData.refundDetails &&
            articleData.refundDetails.length > 0
          ) {
            for (const refund of articleData.refundDetails) {
              if (refund.RefundProofKey) {
                try {
                  const fileResponse =
                    await getArticleService.getFilePreSIgnedUrl({
                      key: refund.RefundProofKey,
                    });
                  const fileType = getFileType(refund.RefundProofKey);
                  if (fileType === "image") {
                    urls[refund.RefundProofKey] = fileResponse;
                  } else if (fileType === "pdf") {
                    pdfUrls[refund.RefundProofKey] = fileResponse;
                  } else {
                    docUrls[refund.RefundProofKey] = fileResponse;
                  }
                } catch (error) {
                  console.error("Error fetching refund proof URL:", error);
                }
              }
            }
          }

          setImageUrls(urls);
          setPdfUrls(pdfUrls);
          setDocumentUrls(docUrls);
        }
      } catch (error) {
        console.error("Error fetching article:", error);
        setArticle(null); // Ensure article is cleared on error
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id]);

  const getFileType = (fileName) => {
    if (!fileName) return null;
    const extension = fileName.split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension)) {
      return "image";
    } else if (extension === "pdf") {
      return "pdf";
    } else if (extension === "docx") {
      return "docx";
    }
    return "other";
  };

  const handleFileClick = (fileKey, fileType) => {
    if (fileType === "image") {
      setSelectedImage(fileKey);
      setOpenImageDialog(true);
    } else {
      setSelectedPdf(fileKey);
      setOpenPdfDialog(true);
    }
  };

  const getFileUrl = (fileKey) => {
    const fileType = getFileType(fileKey);
    if (fileType === "image") {
      return imageUrls[fileKey];
    } else if (fileType === "pdf") {
      return pdfUrls[fileKey];
    } else {
      return documentUrls[fileKey];
    }
  };

  const handleViewRefundProof = (proofKey) => {
    if (proofKey) {
      const fileType = getFileType(proofKey);
      handleFileClick(proofKey, fileType);
    }
  };

  const handleCloseImageDialog = () => {
    setOpenImageDialog(false);
    setSelectedImage(null);
  };

  const handleClosePdfDialog = () => {
    setOpenPdfDialog(false);
    setSelectedPdf(null);
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!article) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          Invoice not found
        </Typography>
      </Box>
    );
  }

  console.log("article", article);

  const paymentStatus = getPaymentStatus(article);

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        maxWidth: "1200px",
        margin: "0 auto",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={{ mb: 2, alignSelf: "flex-start" }}
      >
        Back to List
      </Button>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 4 },
          borderRadius: 2,
          backgroundColor: "background.paper",
          boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.1)",
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              color: "primary.main",
            }}
          >
            Invoice Details
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Chip
              label={paymentStatus.label}
              color={paymentStatus.color}
              size="medium"
            />
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 3,
                backgroundColor: "background.default",
                borderRadius: 2,
                height: "100%",
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
              }}
            >
              <Typography
                variant="h6"
                color="text.secondary"
                gutterBottom
                sx={{ mb: 2 }}
              >
                Journal Information
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body1"
                    sx={{ minWidth: "120px", color: "text.secondary" }}
                  >
                    Journal Name:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    {article.journalName}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body1"
                    sx={{ minWidth: "120px", color: "text.secondary" }}
                  >
                    Publication Type:
                  </Typography>
                  <Chip
                    label={article.publicationType}
                    color="primary"
                    size="small"
                  />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body1"
                    sx={{ minWidth: "120px", color: "text.secondary" }}
                  >
                    Fee Type:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    {article.feeType}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body1"
                    sx={{ minWidth: "120px", color: "text.secondary" }}
                  >
                    Author Name:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    {article.authorName}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body1"
                    sx={{ minWidth: "120px", color: "text.secondary" }}
                  >
                    Payer Name:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    {article.payerName}
                  </Typography>
                </Box>{" "}
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body1"
                    sx={{ minWidth: "120px", color: "text.secondary" }}
                  >
                    Author University:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    {article.authorUniversity}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body1"
                    sx={{ minWidth: "120px", color: "text.secondary" }}
                  >
                    GST Number:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    {article.authorGstNo}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Payment Information */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 3,
                backgroundColor: "background.default",
                borderRadius: 2,
                height: "100%",
              }}
            >
              <Typography
                variant="h6"
                color="text.secondary"
                gutterBottom
                sx={{ mb: 2 }}
              >
                Payment Information
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body1"
                    sx={{ minWidth: "120px", color: "text.secondary" }}
                  >
                    Base Fees:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    {article.currency} {article.baseFees}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body1"
                    sx={{ minWidth: "120px", color: "text.secondary" }}
                  >
                    Tax Rate:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    {article.taxRate}%
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body1"
                    sx={{ minWidth: "120px", color: "text.secondary" }}
                  >
                    Tax Amount:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    {article.currency} {article.taxAmount}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body1"
                    sx={{ minWidth: "120px", color: "text.secondary" }}
                  >
                    Total Fees:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    {article.currency} {article.totalfees}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body1"
                    sx={{ minWidth: "120px", color: "text.secondary" }}
                  >
                    Fees Received:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    {article.currency} {article.feesRecived}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body1"
                    sx={{ minWidth: "120px", color: "text.secondary" }}
                  >
                    Remaining Fees:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    {article.currency} {article.remaningFees}
                  </Typography>
                </Box>

                {article && article?.additionalAmountReceived && (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography
                      variant="body1"
                      sx={{
                        minWidth: "120px",
                        color: "text.secondary",
                        marginRight: "10px",
                      }}
                    >
                      Additional Amount:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                      {article?.additionalAmountReceived}
                    </Typography>
                  </Box>
                )}

                {article && article?.refundAmount && (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography
                      variant="body1"
                      sx={{
                        minWidth: "120px",
                        color: "text.secondary",
                        marginRight: "10px",
                      }}
                    >
                      Refund Amount:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                      {article?.refundAmount}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body1"
                    sx={{ minWidth: "120px", color: "text.secondary" }}
                  >
                    Currency:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    {article.currency}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Additional Information */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 3,
                backgroundColor: "background.default",
                borderRadius: 2,
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
              }}
            >
              <Typography
                variant="h6"
                color="text.secondary"
                gutterBottom
                sx={{ mb: 2 }}
              >
                Additional Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography
                      variant="body1"
                      sx={{ minWidth: "120px", color: "text.secondary" }}
                    >
                      Invoice Type:
                    </Typography>
                    <Chip
                      label={article.invoiceType}
                      color="info"
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography
                      variant="body1"
                      sx={{ minWidth: "120px", color: "text.secondary" }}
                    >
                      Service Name:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                      {article.serviceName}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography
                      variant="body1"
                      sx={{ minWidth: "120px", color: "text.secondary" }}
                    >
                      Country:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                      {article.country}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography
                      variant="body1"
                      sx={{ minWidth: "120px", color: "text.secondary" }}
                    >
                      State:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                      {article.state}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography
                      variant="body1"
                      sx={{ minWidth: "120px", color: "text.secondary" }}
                    >
                      Remark:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                      {article.remark}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Payment History */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 3,
                backgroundColor: "background.default",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h6"
                color="text.secondary"
                gutterBottom
                sx={{ mb: 2 }}
              >
                Payment History
              </Typography>
              {article.payments && article.payments.length > 0 ? (
                article.payments.map((payment, index) => (
                  <Accordion key={payment.paymentId} sx={{ mb: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Grid container alignItems="center" spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="subtitle1">
                            Payment #{index + 1}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2" color="text.secondary">
                            {moment(payment.paymentDate).format("MMMM D, YYYY")}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="subtitle1" color="primary">
                            {article.currency} {payment.amount}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Chip
                            label={
                              payment.utrNo
                                ? "Payment Confirmed"
                                : "Payment Pending"
                            }
                            color={payment.utrNo ? "success" : "warning"}
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" gutterBottom>
                            Payment Details
                          </Typography>
                          <Paper
                            elevation={0}
                            sx={{ p: 2, bgcolor: "background.paper" }}
                          >
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Amount: {article.currency} {payment.amount}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Date:{" "}
                                  {moment(payment.paymentDate).format(
                                    "MMMM D, YYYY"
                                  )}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  UTR Number: {payment.utrNo || "Not provided"}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Credit Date:{" "}
                                  {payment.creditDate
                                    ? moment(payment.creditDate).format(
                                        "MMMM D, YYYY"
                                      )
                                    : "Not provided"}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Payment Mode:{" "}
                                  {payment.paymentMode || "Not provided"}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Bank: {payment.bank || "Not provided"}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Created At: {payment.createdAtDate ? moment(payment.createdAtDate).format("DD-MM-YY hh:mm A") : "Not provided"}
                                </Typography>
                              </Grid>
                              {payment.remark && (
                                <Grid item xs={12}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Remark: {payment.remark}
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>
                          </Paper>
                        </Grid>

                        {/* Invoice Section */}
                        {payment.invoiceKey && (
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                              Invoice
                            </Typography>
                            <Button
                              variant="outlined"
                              startIcon={<PictureAsPdfIcon />}
                              onClick={() =>
                                handleFileClick(payment.invoiceKey, "pdf")
                              }
                              sx={{ mb: 2 }}
                            >
                              View Invoice
                            </Button>
                          </Grid>
                        )}

                        {/* Payment Proofs Section */}
                        {payment.paymentProofFileKeys &&
                          payment.paymentProofFileKeys.length > 0 && (
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" gutterBottom>
                                Payment Proofs
                              </Typography>
                              <Grid container spacing={2}>
                                {payment.paymentProofFileKeys.map(
                                  (proof, proofIndex) => {
                                    const fileType = getFileType(proof);
                                    const fileUrl = getFileUrl(proof);
                                    return (
                                      <Grid
                                        item
                                        key={proofIndex}
                                        xs={6}
                                        sm={4}
                                        md={3}
                                      >
                                        {fileType === "image" ? (
                                          <Button
                                            variant="outlined"
                                            onClick={() =>
                                              handleFileClick(proof, "image")
                                            }
                                            sx={{
                                              p: 1,
                                              width: "100%",
                                              height: "100%",
                                              minHeight: "150px",
                                              display: "flex",
                                              flexDirection: "column",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              borderRadius: 2,
                                            }}
                                          >
                                            {fileUrl ? (
                                              <img
                                                src={fileUrl}
                                                alt={`Payment Proof ${
                                                  proofIndex + 1
                                                }`}
                                                style={{
                                                  maxWidth: "100%",
                                                  maxHeight: "100%",
                                                  objectFit: "contain",
                                                }}
                                              />
                                            ) : (
                                              <CircularProgress size={24} />
                                            )}
                                          </Button>
                                        ) : (
                                          <Button
                                            variant="outlined"
                                            onClick={() =>
                                              handleFileClick(proof, fileType)
                                            }
                                            startIcon={
                                              fileType === "pdf" ? (
                                                <PictureAsPdfIcon />
                                              ) : fileType === "docx" ? (
                                                <DescriptionIcon />
                                              ) : (
                                                <ReceiptIcon />
                                              )
                                            }
                                            sx={{
                                              width: "100%",
                                              height: "100%",
                                              minHeight: "50px",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              borderRadius: 2,
                                            }}
                                          >
                                            {fileUrl ? (
                                              `View ${
                                                fileType === "pdf"
                                                  ? "PDF"
                                                  : fileType === "docx"
                                                  ? "Document"
                                                  : "File"
                                              }`
                                            ) : (
                                              <CircularProgress size={24} />
                                            )}
                                          </Button>
                                        )}
                                      </Grid>
                                    );
                                  }
                                )}
                              </Grid>
                            </Grid>
                          )}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  align="center"
                >
                  No payment history available
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Discount History */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 3,
                backgroundColor: "background.default",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h6"
                color="text.secondary"
                gutterBottom
                sx={{ mb: 2 }}
              >
                Discount History
              </Typography>
              {article.discounts && article.discounts.length > 0 ? (
                article.discounts.map((discount, index) => (
                  <Accordion key={index} sx={{ mb: 2 }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        backgroundColor: "background.paper",
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <Grid container alignItems="center" spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="subtitle1">
                            Discount #{index + 1}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="subtitle1" color="success.main">
                            -{discount.discountAmount}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" color="text.secondary">
                            {moment(discount.appliedOn).format("MMMM D, YYYY")}
                          </Typography>
                        </Grid>
                      </Grid>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Paper
                        elevation={0}
                        sx={{ p: 2, bgcolor: "background.default" }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Discount Amount: -{discount.discountAmount}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Discount Percentage: {discount.discountPercent}%
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              Applied On:{" "}
                              {moment(discount.appliedOn).format(
                                "MMMM D, YYYY, h:mm A"
                              )}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  align="center"
                >
                  No discount history available
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Adjustments */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 3,
                backgroundColor: "background.default",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h6"
                color="text.secondary"
                gutterBottom
                sx={{ mb: 2 }}
              >
                Adjustments
              </Typography>
              {article.adjustments && article.adjustments.length > 0 ? (
                article.adjustments.map((adjustment, index) => (
                  <Accordion key={adjustment.adjustmentId} sx={{ mb: 2 }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        backgroundColor: "background.paper",
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <Grid container alignItems="center" spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="subtitle1">
                            Adjustment #{index + 1}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="subtitle1" color="error.main">
                            -{article.currency} {adjustment.adjustedAmount}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2" color="text.secondary">
                            {moment(adjustment.createdAt).format(
                              "MMMM D, YYYY"
                            )}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2" color="text.secondary">
                            #{article.id} → #{adjustment.toInvoiceId}
                          </Typography>
                        </Grid>
                      </Grid>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Paper
                        elevation={0}
                        sx={{ p: 2, bgcolor: "background.default" }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Adjusted Amount: -{article.currency}{" "}
                              {adjustment.adjustedAmount}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Adjustment ID: {adjustment.adjustmentId}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              Applied On:{" "}
                              {moment(adjustment.createdAt).format(
                                "MMMM D, YYYY, h:mm A"
                              )}
                            </Typography>
                          </Grid>
                          {adjustment.remark && (
                            <Grid item xs={12}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Remark: {adjustment.remark}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  align="center"
                >
                  No adjustments available
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Refund History */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 3,
                backgroundColor: "background.default",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h6"
                color="text.secondary"
                gutterBottom
                sx={{ mb: 2 }}
              >
                Refund History
              </Typography>
              {article.refundDetails && article.refundDetails.length > 0 ? (
                article.refundDetails.map((refund, index) => (
                  <Accordion key={refund.id || index} sx={{ mb: 2 }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        backgroundColor: "background.paper",
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <Grid container alignItems="center" spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="subtitle1">
                            Refund #{index + 1}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="subtitle1" color="error">
                            -{article.currency} {refund.refundAmount}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2" color="text.secondary">
                            {moment(refund.createdAt).format("MMMM D, YYYY")}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Chip
                            label={refund.status}
                            sx={{
                              bgcolor: (() => {
                                switch (refund.status?.toLowerCase()) {
                                  case "pending":
                                    return "warning.main";
                                  case "approved":
                                    return "info.main";
                                  case "rejected":
                                    return "error.main";
                                  case "completed":
                                    return "success.main";
                                  default:
                                    return "grey.500";
                                }
                              })(),
                              color: "white",
                            }}
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Paper
                        elevation={0}
                        sx={{ p: 2, bgcolor: "background.default" }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Refund Amount: {article.currency}{" "}
                              {refund.refundAmount}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Status: {refund.status}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Bank Name: {refund.bankName}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Account Number: {refund.accountNumber}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              IFSC Code: {refund.ifscCode}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Beneficiary Name: {refund.beneficiaryName}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              Reason: {refund.refundReason}
                            </Typography>
                          </Grid>
                          {refund.adminRemark && (
                            <Grid item xs={12}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Admin Remark: {refund.adminRemark}
                              </Typography>
                            </Grid>
                          )}
                          {refund.EditorRemark && (
                            <Grid item xs={12}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Editor Remark: {refund.EditorRemark}
                              </Typography>
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              Created At:{" "}
                              {moment(refund.createdAt).format(
                                "MMMM D, YYYY, h:mm A"
                              )}
                            </Typography>
                          </Grid>
                          {refund.RefundProofKey && (
                            <Grid item xs={12}>
                              <Button
                                variant="outlined"
                                startIcon={<ReceiptIcon />}
                                onClick={() =>
                                  handleViewRefundProof(refund.RefundProofKey)
                                }
                                sx={{ mt: 1 }}
                              >
                                View Refund Proof
                              </Button>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  align="center"
                >
                  No refund history available
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Image Preview Dialog */}
      <Dialog
        open={openImageDialog}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleCloseImageDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Payment Proof
          <IconButton onClick={handleCloseImageDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedImage && imageUrls[selectedImage] && (
            <img
              src={imageUrls[selectedImage]}
              alt="Payment Proof"
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "70vh",
                objectFit: "contain",
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog
        open={openPdfDialog}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClosePdfDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {selectedPdf && getFileType(selectedPdf) === "pdf"
            ? "Invoice"
            : "Document"}
          <IconButton onClick={handleClosePdfDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ height: "80vh" }}>
          {selectedPdf && getFileUrl(selectedPdf) && (
            <iframe
              src={getFileUrl(selectedPdf)}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
              }}
              title={
                getFileType(selectedPdf) === "pdf" ? "Invoice PDF" : "Document"
              }
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SingleSubmittedArticle;
